/**
 * Date helpers for the planning wizard. Dates live in the form as a JS Date in
 * add mode, or as a 'dd-MM-yyyy' string in edit mode (Angular patched strings).
 */

const pad = (n: number) => String(n).padStart(2, '0');

/** Parse a value into a Date. Accepts a Date, 'dd-MM-yyyy' / 'dd/MM/yyyy',
 *  'yyyy-MM-dd' (or ISO), returning null when it can't be parsed. Used to feed
 *  PrimeReact Calendar, which requires Date objects for value/minDate. */
export function parseDate(value: Date | string | null): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;

  const s = String(value).trim();

  // 'dd-MM-yyyy' or 'dd/MM/yyyy'
  const dmy = /^(\d{2})[-/](\d{2})[-/](\d{4})$/.exec(s);
  if (dmy) {
    const [, d, m, y] = dmy;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }

  // 'yyyy-MM-dd' (optionally with time)
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) {
    const [, y, m, d] = iso;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }

  const parsed = new Date(s);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/** Serialize to the API format 'yyyy-MM-dd' (Angular finalSubmit reversal). */
export function toApiDate(value: Date | string | null): string {
  const d = parseDate(value);
  if (!d) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Display as 'dd/MM/yyyy'. Accepts a Date, an ISO / 'yyyy-MM-dd' string, or an
 *  already-formatted 'dd-MM-yyyy' / 'dd/MM/yyyy' string. */
export function displayDate(value: Date | string | null): string {
  if (!value) return '';

  const fromDate = (d: Date) =>
    `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;

  if (value instanceof Date) return fromDate(value);

  const s = String(value).trim();

  // Already 'dd-MM-yyyy' or 'dd/MM/yyyy' -> normalise separators to '/'.
  const dmy = /^(\d{2})[-/](\d{2})[-/](\d{4})$/.exec(s);
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${d}/${m}/${y}`;
  }

  // 'yyyy-MM-dd' (optionally with a time component) -> 'dd/MM/yyyy'.
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) {
    const [, y, m, d] = iso;
    return `${d}/${m}/${y}`;
  }

  // Fallback: let Date parse it.
  const parsed = new Date(s);
  return isNaN(parsed.getTime()) ? s : fromDate(parsed);
}
